import {
  GameInstanceStore,
  audioDeviceNameForInstance,
  shadowCamNameForInstance,
  type GameInstanceSessionRecord,
} from './GameInstanceStore';

export type GameInstanceResourcePool = {
  vmIds: string[];
  shadowCamNames: string[];
  virtualDisplayNames: string[];
  virtualDisplaySerials: string[];
  playjectorPort: number;
  loggingPort: number;
  displayWidth: number;
  displayHeight: number;
  refreshRate: number;
};

export type GameInstanceAllocationRequest = {
  count: number;
  campaignId?: string;
  baseSessionId?: string;
  packageName?: string;
  pool?: Partial<GameInstanceResourcePool>;
};

const letter = (index: number): string => String.fromCharCode(65 + index);

/**
 * Builds a resource pool with `capacity` game instances. Replaces the
 * hardcoded 9-instance `DEFAULT_PLAYMU_RESOURCE_POOL` — capacity is driven by
 * the host's effective max (`hostEffectiveMax`, pool-match-lifecycle Phase 5).
 */
export function buildResourcePool(capacity: number): GameInstanceResourcePool {
  return {
    vmIds: Array.from({ length: capacity }, (_, i) => String(i)),
    shadowCamNames: Array.from(
      { length: capacity },
      (_, i) => `ShadowCam${String(i).padStart(2, '0')}`,
    ),
    virtualDisplayNames: Array.from(
      { length: capacity },
      (_, i) => `Playcast Display ${letter(i)}`,
    ),
    virtualDisplaySerials: Array.from(
      { length: capacity },
      (_, i) => `PLAYCAST-${letter(i)}`,
    ),
    playjectorPort: 27_017,
    loggingPort: 27_016,
    displayWidth: 856,
    displayHeight: 480,
    refreshRate: 60,
  };
}

export const MAX_INSTANCE_CAPACITY = 64;

export class GameInstanceAllocator {
  private capacity: number;
  private defaultPool: GameInstanceResourcePool;

  constructor(
    private readonly store: GameInstanceStore,
    capacity = 9,
  ) {
    this.capacity = Math.min(capacity, MAX_INSTANCE_CAPACITY);
    this.defaultPool = buildResourcePool(this.capacity);
  }

  public getCapacity(): number {
    return this.capacity;
  }

  /**
   * Re-size the resource pool (pool-match-lifecycle Phase 5): the host
   * re-evaluates its effective max when the local `maxPlayers` setting or the
   * hardware cap changes. Only future allocations are affected; already
   * reserved sessions keep their allocated resources.
   */
  public setCapacity(capacity: number): void {
    this.capacity = Math.min(capacity, MAX_INSTANCE_CAPACITY);
    this.defaultPool = buildResourcePool(this.capacity);
  }

  public allocate(request: GameInstanceAllocationRequest): GameInstanceSessionRecord[] {
    if (!Number.isInteger(request.count) || request.count <= 0) {
      throw new Error('Game instance allocation count must be a positive integer');
    }
    const pool = this.mergePool(request.pool);
    this.assertPoolCapacity(pool, request.count);
    const freeIndexes = this.getFreeIndexes(pool);
    if (freeIndexes.length < request.count) {
      throw new Error(
        `Game instance resource pool exhausted: requested ${request.count}, available ${freeIndexes.length}`,
      );
    }
    const sessions = freeIndexes.slice(0, request.count).map((poolIndex, allocationIndex) => ({
      sessionId: this.makeSessionId(request.baseSessionId, allocationIndex),
      gameInstanceIndex: poolIndex,
      launcherInstanceId: pool.vmIds[poolIndex],
      campaignId: request.campaignId,
      packageName: request.packageName,
      shadowCamName: pool.shadowCamNames[poolIndex],
      virtualDisplayName: pool.virtualDisplayNames[poolIndex],
      virtualDisplaySerial: pool.virtualDisplaySerials[poolIndex],
      playjectorPort: pool.playjectorPort,
      loggingPort: pool.loggingPort,
      displayWidth: pool.displayWidth,
      displayHeight: pool.displayHeight,
      refreshRate: pool.refreshRate,
    }));
    return this.store.reserve(sessions);
  }

  /**
   * Android multi-instance allocation (android-multi-instance-host Phase 3):
   * index-only — assigns `gameInstanceIndex` (0..N-1) and the expected
   * `shadowCamName` (`ShadowCam{k+1:02d}` per the frozen naming contract).
   * NO vmId/display/serial/port synthesis: the machine owns the
   * monitor/camera/VM lifecycle and echoes the bound camera back on
   * `instanceLaunched`. `virtualDisplayName` stays '' (unset sentinel —
   * skipped by the store's uniqueness checks) until C++ reporting lands.
   */
  public allocateForAndroidLaunch(request: {
    count: number;
    campaignId?: string;
    packageName?: string;
  }): GameInstanceSessionRecord[] {
    if (!Number.isInteger(request.count) || request.count <= 0) {
      throw new Error('Game instance allocation count must be a positive integer');
    }
    if (request.count > this.capacity) {
      this.throwCapacity(request.count);
    }
    const usedIndexes = new Set(this.store.getSessions().map((s) => s.gameInstanceIndex));
    const freeIndexes: number[] = [];
    for (let index = 0; index < this.capacity && freeIndexes.length < request.count; index += 1) {
      if (!usedIndexes.has(index)) freeIndexes.push(index);
    }
    if (freeIndexes.length < request.count) {
      throw new Error(
        `Game instance resource pool exhausted: requested ${request.count}, available ${freeIndexes.length}`,
      );
    }
    const sessions = freeIndexes.map((gameInstanceIndex, allocationIndex) => ({
      sessionId: this.makeSessionId(
        request.campaignId ? `gi-${request.campaignId}` : undefined,
        allocationIndex,
      ),
      gameInstanceIndex,
      shadowCamName: shadowCamNameForInstance(gameInstanceIndex),
      audioDeviceName: audioDeviceNameForInstance(gameInstanceIndex),
      virtualDisplayName: '',
      campaignId: request.campaignId,
      packageName: request.packageName,
    }));
    return this.store.reserve(sessions);
  }

  public release(sessionId: string): void {
    this.store.release(sessionId);
  }

  private mergePool(pool?: Partial<GameInstanceResourcePool>): GameInstanceResourcePool {
    return { ...this.defaultPool, ...pool };
  }

  private assertPoolCapacity(pool: GameInstanceResourcePool, requested: number): void {
    const completeInstanceCount = Math.min(
      this.capacity,
      pool.vmIds.length,
      pool.shadowCamNames.length,
      pool.virtualDisplayNames.length,
      pool.virtualDisplaySerials.length,
    );
    if (requested > completeInstanceCount) this.throwCapacity(requested, completeInstanceCount);
  }

  private throwCapacity(requested: number, capacity = this.capacity): never {
    throw new Error(
      `Game instance resource pool only has ${capacity} complete instance(s), requested ${requested}`,
    );
  }

  private getFreeIndexes(pool: GameInstanceResourcePool): number[] {
    const used = this.store.getSessions();
    const launcherIds = new Set(used.map((s) => s.launcherInstanceId));
    const cameras = new Set(used.map((s) => s.shadowCamName));
    const displays = new Set(used.map((s) => s.virtualDisplayName));
    const serials = new Set(used.map((s) => s.virtualDisplaySerial));
    const indexes = new Set(used.map((s) => s.gameInstanceIndex));
    return pool.vmIds
      .map((_, index) => index)
      .filter((index) =>
        !indexes.has(index)
        && !launcherIds.has(pool.vmIds[index])
        && !cameras.has(pool.shadowCamNames[index])
        && !displays.has(pool.virtualDisplayNames[index])
        && !serials.has(pool.virtualDisplaySerials[index]),
      );
  }

  private makeSessionId(base: string | undefined, index: number): string {
    if (base) return index === 0 ? base : `${base}-${index + 1}`;
    return this.store.nextFallbackSessionId();
  }
}
